import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAtom, useAtomValue } from "jotai";
import { FormControlLabel, Switch, Tooltip } from "@mui/material";
import styled from "@emotion/styled";
import {
  skipInactivityEnabledAtom,
  videoPlayerAtom,
} from "../atoms/playback-timestamp-atom";
import { parseGptCommandsResponse } from "../utils/helpers";
import type { InteractionLog } from "../utils/types";
import {
  SKIP_JITTER_MS,
  computeActiveWindows,
  inactiveGaps,
  nextActiveTime,
} from "../routes/recording/active-windows";

/**
 * Adds interaction marks + "skip inactivity" playback to the recording video.
 * Reads the shared video.js player instance from `videoPlayerAtom` (set on
 * mount in videojs.tsx) — there is no new sync path, marks/skip and the
 * interaction list both key off `relativeTimestamp`.
 *
 * Rendered as a sibling of <VideoJS> inside RecordingVideoPlayer.
 */
const RecordingTimelineEnhancements = ({
  interactionLogs,
  startedTimeInEpoch,
  finishedTimeInEpoch,
}: {
  interactionLogs: InteractionLog[];
  // Session start/finish (epoch seconds), as shown in the Session Info tab:
  // started from the run, finished from runData. Used to scale real session
  // time onto the (possibly time-compressed) video timeline.
  startedTimeInEpoch?: number;
  finishedTimeInEpoch?: number;
}) => {
  const player = useAtomValue(videoPlayerAtom) as any;
  const [skipEnabled, setSkipEnabled] = useAtom(skipInactivityEnabledAtom);

  const [durationMs, setDurationMs] = useState(0);
  const [seekBarEl, setSeekBarEl] = useState<HTMLElement | null>(null);

  // Track the player's duration + locate the seek bar element to portal into.
  useEffect(() => {
    if (!player) {
      setDurationMs(0);
      setSeekBarEl(null);
      return;
    }

    const sync = () => {
      const d = player.duration?.();
      if (Number.isFinite(d) && d > 0) setDurationMs(d * 1000);
      const el = getSeekBarEl(player);
      if (el) setSeekBarEl(el);
    };

    player.ready(sync);
    player.on("loadedmetadata", sync);
    player.on("durationchange", sync);
    sync();

    return () => {
      player.off?.("loadedmetadata", sync);
      player.off?.("durationchange", sync);
    };
  }, [player]);

  // Real session duration (ms) from the Session Info start/finish timestamps.
  const realSessionMs = useMemo(() => {
    if (
      startedTimeInEpoch != null &&
      finishedTimeInEpoch != null &&
      finishedTimeInEpoch > startedTimeInEpoch
    ) {
      return (finishedTimeInEpoch - startedTimeInEpoch) * 1000;
    }
    return null;
  }, [startedTimeInEpoch, finishedTimeInEpoch]);

  // Interaction `relativeTimestamp`s are real wall-clock time from session
  // start, but the recorded video can be time-compressed (captured at a higher
  // effective fps), so e.g. a 3.5-minute session is encoded as an 81-second
  // clip. Placing marks at `relativeTimestamp` then overflows the seek bar and
  // late interactions vanish. Scale real time onto video time with a single
  // linear factor: videoDuration / realSessionDuration. The real session length
  // comes from the run's start/finish timestamps; if those are missing we fall
  // back to the latest interaction timestamp. We clamp the factor to <= 1, so
  // real-time recordings are left untouched and only sped-up videos get
  // remapped.
  const realToVideoScale = useMemo(() => {
    if (durationMs <= 0) return 1;

    let denominatorMs = realSessionMs;
    if (denominatorMs == null) {
      let maxRelativeTimestamp = 0;
      for (const log of interactionLogs) {
        if (Number.isFinite(log.relativeTimestamp)) {
          maxRelativeTimestamp = Math.max(
            maxRelativeTimestamp,
            log.relativeTimestamp
          );
        }
      }
      denominatorMs = maxRelativeTimestamp;
    }

    if (denominatorMs <= 0) return 1;
    return Math.min(durationMs / denominatorMs, 1);
  }, [interactionLogs, durationMs, realSessionMs]);

  // Interaction logs expressed in video time (the domain the seek bar, marks and
  // skip windows all operate in). A no-op when realToVideoScale === 1.
  const videoTimeLogs = useMemo(
    () =>
      realToVideoScale === 1
        ? interactionLogs
        : interactionLogs.map((log) => ({
            ...log,
            relativeTimestamp: log.relativeTimestamp * realToVideoScale,
          })),
    [interactionLogs, realToVideoScale]
  );

  const windows = useMemo(
    () => computeActiveWindows(videoTimeLogs, durationMs),
    [videoTimeLogs, durationMs]
  );

  const gaps = useMemo(
    () => inactiveGaps(windows, durationMs),
    [windows, durationMs]
  );

  // Start playback at the first active moment (only while still at the start).
  useEffect(() => {
    if (!player || !skipEnabled || windows.length === 0) return;

    const seekToFirst = () => {
      if (player.currentTime() < 0.25 && windows[0].start > 0) {
        player.currentTime(windows[0].start / 1000);
      }
    };

    player.ready(seekToFirst);
    player.on("loadedmetadata", seekToFirst);
    return () => {
      player.off?.("loadedmetadata", seekToFirst);
    };
  }, [player, skipEnabled, windows]);

  // Skip inactivity during active playback.
  useEffect(() => {
    if (!player || !skipEnabled || windows.length === 0 || durationMs <= 0)
      return;

    const onTimeUpdate = () => {
      // Don't fight the user inspecting a frame or scrubbing into dead time.
      if (player.paused() || player.scrubbing?.()) return;

      const currentMs = player.currentTime() * 1000;
      const target = nextActiveTime(windows, currentMs, durationMs);
      if (target != null && target - currentMs > SKIP_JITTER_MS) {
        player.currentTime(target / 1000);
      }
    };

    player.on("timeupdate", onTimeUpdate);
    return () => {
      player.off?.("timeupdate", onTimeUpdate);
    };
  }, [player, skipEnabled, windows, durationMs]);

  if (interactionLogs.length === 0) return null;

  return (
    <>
      {seekBarEl != null &&
        durationMs > 0 &&
        createPortal(
          <SeekBarMarks
            player={player}
            durationMs={durationMs}
            gaps={gaps}
            interactionLogs={videoTimeLogs}
          />,
          seekBarEl
        )}
      <ToggleWrapper>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={skipEnabled}
              onChange={(e) => {
                setSkipEnabled(e.target.checked);
              }}
            />
          }
          label="Skip inactivity"
        />
      </ToggleWrapper>
    </>
  );
};

interface Mark {
  t: number;
  command: string;
  ariaLabel: string;
}

const clampMark = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

function SeekBarMarks({
  player,
  durationMs,
  gaps,
  interactionLogs,
}: {
  player: any;
  durationMs: number;
  gaps: Array<{ start: number; end: number }>;
  interactionLogs: InteractionLog[];
}) {
  // One mark per interaction. We only drop a mark when its timestamp can't be
  // positioned at all (non-finite). Out-of-range timestamps are clamped onto
  // the bar rather than dropped, so every interaction in the list also gets a
  // mark — typeText/typing interactions are captured via a separate event and
  // can carry a timestamp just past the measured video duration (or, before the
  // session start, slightly negative), which previously made them vanish.
  const marks: Mark[] = useMemo(
    () =>
      interactionLogs
        .filter((interaction) => Number.isFinite(interaction.relativeTimestamp))
        .map((interaction) => {
          const t = clampMark(interaction.relativeTimestamp, 0, durationMs);
          const command = markCommand(interaction);
          return {
            t,
            command,
            ariaLabel:
              command !== ""
                ? command
                : `Interaction at ${(t / 1000).toFixed(1)}s`,
          };
        }),
    [interactionLogs, durationMs]
  );

  return (
    <MarksOverlay>
      {gaps.map((g, i) => (
        <GapShade
          key={`gap-${i}`}
          style={{
            left: `${(g.start / durationMs) * 100}%`,
            width: `${((g.end - g.start) / durationMs) * 100}%`,
          }}
        />
      ))}
      {marks.map((mark, i) => (
        <Tooltip
          key={`mark-${i}`}
          title={
            mark.command !== "" ? (
              <TooltipCommand>{mark.command}</TooltipCommand>
            ) : (
              ""
            )
          }
          placement="top"
          arrow
          slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
        >
          <Tick
            aria-label={mark.ariaLabel}
            style={{ left: `calc(${(mark.t / durationMs) * 100}% - 1px)` }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              player.currentTime(mark.t / 1000);
            }}
          />
        </Tooltip>
      ))}
    </MarksOverlay>
  );
}

// The command executed at an interaction, for the hover popover. Action
// interactions store it in `gptCommands`; typeText/typing interactions have no
// gptCommands, so we surface the typed text (then fall back to appetizeCommands).
function markCommand(interaction: InteractionLog): string {
  const command = parseGptCommandsResponse(
    interaction.gptCommands ?? []
  ).commands.trim();
  if (command !== "") return command;

  const typed = interaction as { type?: string; text?: string };
  if (typed.type === "typeText" && typeof typed.text === "string") {
    return `typeText: ${typed.text}`;
  }

  const appetizeCommands = interaction.appetizeCommands ?? [];
  if (appetizeCommands.length > 0) return appetizeCommands.join("\n");

  return "";
}

function getSeekBarEl(player: any): HTMLElement | null {
  try {
    const seekBar =
      player?.controlBar?.progressControl?.seekBar ??
      player?.controlBar?.getChild?.("progressControl")?.getChild?.("seekBar");
    return (seekBar?.el?.() as HTMLElement | undefined) ?? null;
  } catch {
    return null;
  }
}

const TooltipCommand = styled.div`
  font-family: "Roboto Mono", monospace;
  font-size: 11px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
`;

const MarksOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
`;

const GapShade = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  pointer-events: none;
`;

const Tick = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 1px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  pointer-events: auto;

  &:hover {
    background: #90caf9;
  }
`;

const ToggleWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  padding: 0 10px 0 4px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;

  .MuiFormControlLabel-label {
    font-size: 12px;
  }
`;

export default RecordingTimelineEnhancements;
